import { Link, type To } from 'react-router-dom';

import { Anchor, Badge, Tooltip } from '@mantine/core';

import { hasRegistryOrderReply, type RegistryOrder } from '@/types/registry-documents';
import { formatRegistryMessageTime } from './RegistryOrderMessages';

/**
 * The "Reply" pill an order carries while the research desk has written
 * without sending a document, nobody has answered, and the order is still
 * waiting: a quote to accept or decline, a question to answer. Nothing
 * once it is answered, or the order is delivered or closed.
 *
 * The pill opens Armadillo's conversation on the report, pointed at that
 * order: the answer itself is written there.
 */
export function RegistryOrderReplyBadge({
  order,
  to,
}: Readonly<{
  order: RegistryOrder;
  /** Where the conversation is: the report page's aside, on this order. */
  to: To;
}>) {
  if (!hasRegistryOrderReply(order)) {
    return null;
  }
  const when = formatRegistryMessageTime(order.lastReplyAt);
  return (
    <Tooltip
      label={`The research desk wrote${when ? ` on ${when}` : ''} without sending a document: a quote or a question. Open the conversation to read it and reply.`}
      // Hover alone leaves the sentence out of reach of a keyboard and a
      // touch screen, so the link takes focus and opens on that too.
      events={{ hover: true, focus: true, touch: true }}
    >
      <Anchor
        component={Link}
        to={to}
        underline="never"
        onClick={(event) => event.stopPropagation()}
        data-testid={`registry-order-reply-${order.id}`}
      >
        <Badge size="sm" variant="light" color="orange" style={{ cursor: 'pointer' }}>
          Reply
        </Badge>
      </Anchor>
    </Tooltip>
  );
}
